import { useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Textarea } from "@chakra-ui/react";
import { uploadRosterToHie } from "../../../api/internal";
import DrawerForm from "../../shared/form/Drawer";
import { Label } from "../../shared/Label";
import useMetriportToast from "../../shared/toast";
import { captureAndDisplayError } from "../../shared/util";

const formSchema = z.object({ config: z.string() });
type FormData = z.infer<typeof formSchema>;

export default function RosterUploadForm({
  isOpen,
  onClose,
  cxId,
}: {
  isOpen: boolean;
  onClose: () => void;
  cxId: string;
}) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const toast = useMetriportToast();

  async function handleSubmit(data: FormData) {
    setIsSubmitting(true);
    try {
      await uploadRosterToHie({ cxId, config: data.config });
      toast.success({ title: "Roster uploaded" });
      onClose();
    } catch (error) {
      const axiosError = error as {
        response?: { data?: { detail?: string } };
        message?: string;
      };
      const errorMsg =
        axiosError?.response?.data?.detail ?? axiosError?.message ?? "Failed to upload roster";
      toast.error({ title: errorMsg, duration: 10000 });
      captureAndDisplayError({
        error,
        msg: errorMsg,
        context: "uploadRoster",
        captureMsg: "Upload roster failed",
        toast,
        hideDisplay: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <DrawerForm<FormData>
      title="Upload Roster"
      isOpen={isOpen}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      onClose={onClose}
      resolver={zodResolver(formSchema)}
      defaultValues={{ config: "{}" }}
      btSaveId="roster-upload-save"
      btCloseId="roster-upload-close"
    >
      {({ register }) => (
        <Box>
          <Label>Config (JSON)</Label>
          <Textarea
            {...register("config")}
            fontFamily="mono"
            fontSize="sm"
            rows={10}
            placeholder="{}"
          />
        </Box>
      )}
    </DrawerForm>
  );
}
